import { createAppRequest } from '@/services/request';
import type {
  Company,
  Issue,
  Opportunity,
  OperatingSnapshot,
  PortfolioStatus,
  Report,
  Risk,
} from '@/domain/portfolio/types';

export type CompanyDetail = Company & {
  organization?: Company['organization'];
  contacts?: Company['contacts'];
  baseline?: {
    projectId: string;
    thesis: string;
    keyRisk: string;
    judgement: string;
    decisionConditions: Array<{
      title: string;
      required: boolean;
      status: string;
      owner: string;
      dueDate: string;
    }>;
  };
};

export interface PortfolioValuationDto {
  uuid: string;
  companyUuid: string;
  investmentRelationUuid?: string | null;
  valuationDate: string;
  validUntil?: string | null;
  method: string;
  basis?: string | null;
  fairValueSource?: string | null;
  evidenceAttachmentUuid?: string | null;
  value: number;
  status: string;
  changeNote?: string | null;
  createdByMemberUuid?: string | null;
  effectiveByMemberUuid?: string | null;
  effectiveAt?: string | null;
}

/** 被投企业分红登记（进基金现金台账 sourceType=dividend，计入回款与待分配）。 */
export interface PortfolioDividendDto {
  uuid: string;
  companyUuid: string;
  relationUuid?: string | null;
  fundUuid?: string | null;
  fundNameSnapshot?: string;
  amount: number;
  declaredDate?: string | null;
  receivedDate: string;
  remark?: string | null;
  attachmentUuid?: string | null;
}

/** 投后事件（融资/并购/诉讼/股东变化/高管变化/知情权请求/其他）。 */
export interface PortfolioEventDto {
  uuid: string;
  companyUuid: string;
  eventType: string;
  title: string;
  occurredAt: string;
  description?: string | null;
  status: string;
  owner: string;
  attachmentUuid?: string | null;
  createdAt?: string;
}

export interface PortfolioHoldingMovementDto {
  uuid: string;
  companyUuid: string;
  relationUuid: string;
  movementType: string;
  movementDate: string;
  holdingDelta: number;
  amountDelta: number;
  reason: string;
}

export interface PortfolioAmendmentDto {
  id: string;
  transactionId: string;
  type: string;
  title: string;
  summary?: string;
  signedAt?: string;
  owner?: string;
  status: string;
  affectsAmount?: boolean;
  affectsHolding?: boolean;
  amountAfter?: number | null;
  holdingAfter?: number | null;
  writebackCompleted?: boolean;
  fund?: string;
  sourceProjectUuid?: string | null;
}

export interface InvestmentRelationDto {
  id: string;
  companyId: string;
  fundId: string;
  fundUuid?: string;
  fundNameSnapshot?: string;
  round: string;
  amount: number;
  postValuation: number;
  holding: number;
  investedAt: string;
  boardSeat: string;
}

/** 投后三会（董事会 / 股东会 / 监事会）扁平记录。 */
export type GovernanceMeeting = {
  uuid: string;
  companyUuid: string;
  meetingType: string;
  heldAt: string;
  subject: string;
  resolution: string | null;
  status: string;
  owner: string;
  attachmentUuid: string | null;
  voteResult: string;
  attendingMemberUuids: string[] | null;
  boardAppointmentUuid: string | null;
  attendeeName: string;
  relatedIssueUuid: string | null;
  relatedRiskUuid: string | null;
};

/** 财报三表结构化记录（人工录入或 Agent 解析后写入）。 */
export type FinancialStatement = {
  uuid: string;
  companyUuid: string;
  period: string;
  currency: string;
  revenue: number | null;
  netProfit: number | null;
  grossProfit: number | null;
  grossMargin: number | null;
  netMargin: number | null;
  revenueGrowthYoy: number | null;
  goodwill: number | null;
  cashAndCashEquivalents: number | null;
  currentAssets: number | null;
  currentLiabilities: number | null;
  currentRatio: number | null;
  debtToAssetRatio: number | null;
  cashFromSales: number | null;
  cashPaidForPurchases: number | null;
  totalAssets: number | null;
  totalLiabilities: number | null;
  equity: number | null;
  operatingCashFlow: number | null;
  investingCashFlow: number | null;
  financingCashFlow: number | null;
  source: string;
  reportedAt: string;
  attachmentUuid: string | null;
  remark: string;
};

/** 同比 / 环比（只读计算，不落库）。 */
export type FinancialTrend = {
  period: string;
  previousPeriod: string | null;
  lastYearPeriod: string | null;
  revenue: { current: number | null; qoq: number | null; yoy: number | null };
  netProfit: { current: number | null; qoq: number | null; yoy: number | null };
  totalAssets: { current: number | null; qoq: number | null; yoy: number | null };
  equity: { current: number | null; qoq: number | null; yoy: number | null };
  operatingCashFlow: { current: number | null; qoq: number | null; yoy: number | null };
  limitation: string;
};

/** 影子上市公司对标记录。 */
export type Benchmark = {
  uuid: string;
  companyUuid: string;
  benchmarkName: string;
  benchmarkCode: string;
  period: string;
  metric: string;
  companyValue: number | null;
  benchmarkValue: number | null;
  deviationPercent: number | null;
  alert: boolean;
  remark: string;
};

/** 估值穿透结果（只读计算）。 */
export type ValuationPenetration = {
  items: {
    companyUuid: string;
    fundUuid: string | null;
    fundName: string;
    round: string;
    companyValuation: number;
    valuationDate: string | null;
    holdingRatio: number;
    penetratedValue: number;
  }[];
  total: number;
  limitation: string;
};

export type RegistrationChange = { id: string; companyId: string; changeType: string; before: string; after: string; changeDate: string; status: string; attachmentUuid?: string };
export type ReportSubmission = { id: string; companyId: string; period: string; reportType: string; dueDate: string; submittedAt?: string; status: string; reminderCount: number; attachmentUuid?: string; /** 读时计算：截止日已过且未提交 */ overdue?: boolean };
export type BoardAppointment = { id: string; companyId: string; appointingParty: string; personName: string; position: string; appointedAt: string; expiryAt?: string; status: string };
export type AnomalyRuleDto = { id: string; companyUuid?: string; metric: string; operator: string; threshold: number; createIssue: boolean; remark: string };
/** 按基金切分的组合绩效（IRR 为 XIRR 近似，未分配估值作期末流入）。 */
export type FundPerformanceRow = {
  fundUuid: string;
  fundName: string;
  invested: number;
  dividends: number;
  exitProceeds: number;
  recovered: number;
  unrealized: number;
  tvpi: number;
  dpi: number;
  irr: number | null;
  hasCashFlow: boolean;
};
export type PortfolioOverview = {
  activeCount: number;
  exitedCount: number;
  invested: number;
  dividends: number;
  exitProceeds: number;
  recovered: number;
  dpi: number;
  tvpi: number;
  performanceScope: 'portfolio-net';
  averageHoldingDays: number;
  valuationAsOf?: string;
  performanceNote?: string;
  funds?: FundPerformanceRow[];
};

export const portfolioApi = {
  listCompanies: () => createAppRequest()<Company[]>('/bff/portfolio/companies'),
  getOverview: () => createAppRequest()<PortfolioOverview>('/bff/portfolio/overview'),

  createCompany: (data: Record<string, unknown>) =>
    createAppRequest()<Company>('/bff/portfolio/companies', { method: 'POST', data }),

  getCompany: (uuid: string) => createAppRequest()<CompanyDetail>(`/bff/portfolio/companies/${uuid}`),

  updateCompanyStatus: (uuid: string, status: PortfolioStatus) =>
    createAppRequest()<Company>(`/bff/portfolio/companies/${uuid}/status`, { method: 'PATCH', data: { status } }),

  listRelations: () => createAppRequest()<InvestmentRelationDto[]>('/bff/portfolio/relations'),

  listValuations: (companyUuid: string) =>
    createAppRequest()<PortfolioValuationDto[]>('/bff/portfolio/valuations', { params: { companyUuid } }),

  // ── 投后洞察：三会 / 财报三表 / 影子对标 / 估值穿透 ──
  listMeetings: (companyUuid: string) =>
    createAppRequest()<GovernanceMeeting[]>(`/bff/portfolio/companies/${companyUuid}/meetings`),

  createMeeting: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<GovernanceMeeting>(`/bff/portfolio/companies/${companyUuid}/meetings`, {
      method: 'POST',
      data,
    }),

  createIssueFromMeeting: (
    companyUuid: string,
    meetingUuid: string,
    data?: { title?: string; type?: string; importance?: string; riskLevel?: string },
  ) =>
    createAppRequest()<unknown>(
      `/bff/portfolio/companies/${companyUuid}/meetings/${meetingUuid}/issue`,
      { method: 'POST', data: data ?? {} },
    ),

  createRiskFromMeeting: (
    companyUuid: string,
    meetingUuid: string,
    data?: { title?: string; type?: string; level?: string },
  ) =>
    createAppRequest()<unknown>(
      `/bff/portfolio/companies/${companyUuid}/meetings/${meetingUuid}/risk`,
      { method: 'POST', data: data ?? {} },
    ),

  listStatements: (companyUuid: string) =>
    createAppRequest()<FinancialStatement[]>(`/bff/portfolio/companies/${companyUuid}/statements`),

  createStatement: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<FinancialStatement>(`/bff/portfolio/companies/${companyUuid}/statements`, {
      method: 'POST',
      data,
    }),

  statementTrend: (companyUuid: string, period: string) =>
    createAppRequest()<FinancialTrend>(`/bff/portfolio/companies/${companyUuid}/statements/trend`, {
      params: { period },
    }),

  listBenchmarks: (companyUuid: string) =>
    createAppRequest()<Benchmark[]>(`/bff/portfolio/companies/${companyUuid}/benchmarks`),

  createBenchmark: (companyUuid: string, data: Record<string, unknown>) =>
    createAppRequest()<Benchmark>(`/bff/portfolio/companies/${companyUuid}/benchmarks`, {
      method: 'POST',
      data,
    }),

  valuationPenetration: (companyUuid?: string) =>
    createAppRequest()<ValuationPenetration>('/bff/portfolio/valuation-penetration', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  listRegistrationChanges: (companyUuid: string) =>
    createAppRequest()<RegistrationChange[]>('/bff/portfolio/registration-changes', { params: { companyUuid } }),
  createRegistrationChange: (data: Record<string, unknown>) =>
    createAppRequest()<RegistrationChange>('/bff/portfolio/registration-changes', { method: 'POST', data }),
  updateRegistrationChange: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<RegistrationChange>(`/bff/portfolio/registration-changes/${id}`, { method: 'PATCH', data }),
  listReportSubmissions: (companyUuid: string) =>
    createAppRequest()<ReportSubmission[]>('/bff/portfolio/report-submissions', { params: { companyUuid } }),
  createReportSubmission: (data: Record<string, unknown>) =>
    createAppRequest()<ReportSubmission>('/bff/portfolio/report-submissions', { method: 'POST', data }),
  updateReportSubmission: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<ReportSubmission>(`/bff/portfolio/report-submissions/${id}`, { method: 'PATCH', data }),
  listBoardAppointments: (companyUuid: string) =>
    createAppRequest()<BoardAppointment[]>('/bff/portfolio/board-appointments', { params: { companyUuid } }),
  createBoardAppointment: (data: Record<string, unknown>) =>
    createAppRequest()<BoardAppointment>('/bff/portfolio/board-appointments', { method: 'POST', data }),
  updateBoardAppointment: (id: string, data: Record<string, unknown>) =>
    createAppRequest()<BoardAppointment>(`/bff/portfolio/board-appointments/${id}`, { method: 'PATCH', data }),
  listAnomalyRules: (companyUuid: string) =>
    createAppRequest()<AnomalyRuleDto[]>('/bff/portfolio/anomaly-rules', { params: { companyUuid } }),
  createAnomalyRule: (data: Record<string, unknown>) =>
    createAppRequest()<AnomalyRuleDto>('/bff/portfolio/anomaly-rules', { method: 'POST', data }),

  createValuation: (
    data: Omit<
      PortfolioValuationDto,
      | 'uuid'
      | 'status'
      | 'createdByMemberUuid'
      | 'effectiveByMemberUuid'
      | 'effectiveAt'
    >,
  ) =>
    createAppRequest()<PortfolioValuationDto>('/bff/portfolio/valuations', { method: 'POST', data }),

  makeValuationEffective: (uuid: string) =>
    createAppRequest()<PortfolioValuationDto>(`/bff/portfolio/valuations/${uuid}/effective`, {
      method: 'POST',
    }),

  listHoldingMovements: (relationUuid: string) =>
    createAppRequest()<PortfolioHoldingMovementDto[]>(`/bff/portfolio/holding-movements/${relationUuid}`),

  listCompanyHoldingMovements: (companyUuid: string) =>
    createAppRequest()<PortfolioHoldingMovementDto[]>(
      `/bff/portfolio/companies/${companyUuid}/holding-movements`,
    ),

  listCompanyAmendments: (companyUuid: string) =>
    createAppRequest()<PortfolioAmendmentDto[]>(`/bff/portfolio/companies/${companyUuid}/amendments`),

  listCompanyDocuments: (companyUuid: string) =>
    createAppRequest()<
      Array<{
        uuid: string;
        fileName: string;
        docType?: string;
        bizType: string;
        bizUuid: string;
        stageCategory: string;
        sourceLabel: string;
        remark?: string;
        sizeBytes?: number;
        createdAt?: string;
      }>
    >(`/bff/portfolio/companies/${companyUuid}/documents`),

  listDocumentCenter: (projectUuid: string) =>
    createAppRequest()<
      Array<{
        uuid: string;
        fileName: string;
        docType?: string;
        bizType: string;
        bizUuid: string;
        stageCategory: string;
        sourceLabel: string;
        remark?: string;
        sizeBytes?: number;
        createdAt?: string;
      }>
    >('/bff/investment/document-center', {
      method: 'GET',
      params: { projectUuid },
    }),

  createHoldingMovement: (data: Omit<PortfolioHoldingMovementDto, 'uuid'>) =>
    createAppRequest()<PortfolioHoldingMovementDto>('/bff/portfolio/holding-movements', { method: 'POST', data }),

  listOpportunities: () => createAppRequest()<Opportunity[]>('/bff/portfolio/opportunities'),

  createOpportunity: (
    companyUuid: string,
    kind: Opportunity['kind'],
    details?: {
      signal?: string;
      recommendedMethod?: string;
      priority?: string;
      expectedExitAmount?: number;
      timeWindow?: string;
      judgmentConclusion?: string;
      suggestedAmount?: number;
      suggestedRound?: string;
      rationale?: string;
    },
  ) =>
    createAppRequest()<Opportunity>('/bff/portfolio/opportunities', {
      method: 'POST',
      data: { companyUuid, kind, ...details },
    }),

  listSnapshots: (companyUuid?: string) =>
    createAppRequest()<OperatingSnapshot[]>('/bff/portfolio/snapshots', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createSnapshot: (data: Omit<OperatingSnapshot, 'id' | 'updatedAt'> & { companyId: string }) =>
    createAppRequest()<OperatingSnapshot>('/bff/portfolio/snapshots', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        period: data.period,
        revenue: data.revenue,
        budgetRevenue: data.budgetRevenue,
        grossMargin: data.grossMargin,
        cashFlow: data.cashFlow,
        cashBalance: data.cashBalance,
        orders: data.orders,
        rndRatio: data.rndRatio,
        budgetVariance: data.budgetVariance,
        anomalies: data.anomalies,
      },
    }),

  updateSnapshot: (uuid: string, data: Partial<OperatingSnapshot>) =>
    createAppRequest()<OperatingSnapshot>(`/bff/portfolio/snapshots/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listIssues: (companyUuid?: string) =>
    createAppRequest()<Issue[]>('/bff/portfolio/issues', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createIssue: (data: Omit<Issue, 'id' | 'createdAt'> & { companyId: string }) =>
    createAppRequest()<Issue>('/bff/portfolio/issues', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        title: data.title,
        type: data.type,
        importance: data.importance,
        riskLevel: data.riskLevel,
        status: data.status,
        owner: data.owner,
        deadline: data.deadline,
        nextAction: data.nextAction,
        summary: data.summary,
      },
    }),

  updateIssue: (uuid: string, data: Partial<Issue>) =>
    createAppRequest()<Issue>(`/bff/portfolio/issues/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listRisks: (companyUuid?: string) =>
    createAppRequest()<Risk[]>('/bff/portfolio/risks', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createRisk: (data: Omit<Risk, 'id' | 'createdAt'> & { companyId: string }) =>
    createAppRequest()<Risk>('/bff/portfolio/risks', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        issueUuid: data.issueId,
        title: data.title,
        type: data.type,
        level: data.level,
        status: data.status,
        owner: data.owner,
        deadline: data.deadline,
        response: data.response,
      },
    }),

  updateRisk: (uuid: string, data: Partial<Risk>) =>
    createAppRequest()<Risk>(`/bff/portfolio/risks/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listReports: (companyUuid?: string) =>
    createAppRequest()<Report[]>('/bff/portfolio/reports', {
      params: companyUuid ? { companyUuid } : undefined,
    }),

  createReport: (data: {
    companyId: string;
    name: string;
    type?: Report['type'];
    period?: string;
    owner?: string;
    deadline?: string;
    judgement?: string;
    /** 修订版本：被更正的已发布报告 UUID。 */
    correctsReportId?: string;
    correctionReason?: string;
  }) =>
    createAppRequest()<Report>('/bff/portfolio/reports', {
      method: 'POST',
      data: {
        companyUuid: data.companyId,
        name: data.name,
        type: data.type,
        period: data.period,
        owner: data.owner,
        deadline: data.deadline,
        judgement: data.judgement,
        correctsReportUuid: data.correctsReportId,
        correctionReason: data.correctionReason,
      },
    }),

  /** 分红登记（需 canGovernanceEdit）；挂基金时同步入账基金现金台账。 */
  createDividend: (
    companyUuid: string,
    data: {
      relationUuid?: string;
      amount: number;
      receivedDate: string;
      declaredDate?: string;
      remark?: string;
      attachmentUuid?: string;
    },
  ) =>
    createAppRequest()<PortfolioDividendDto>('/bff/portfolio/dividends', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  listDividends: (companyUuid: string) =>
    createAppRequest()<PortfolioDividendDto[]>('/bff/portfolio/dividends', {
      params: { companyUuid },
    }),

  /** 投后事件登记（记录型；工作台自动生成待办）。 */
  createEvent: (
    companyUuid: string,
    data: {
      title: string;
      eventType: string;
      occurredAt?: string;
      description?: string;
      owner?: string;
      attachmentUuid?: string;
    },
  ) =>
    createAppRequest()<PortfolioEventDto>('/bff/portfolio/events', {
      method: 'POST',
      data: { companyUuid, ...data },
    }),

  listEvents: (companyUuid: string) =>
    createAppRequest()<PortfolioEventDto[]>('/bff/portfolio/events', {
      params: { companyUuid },
    }),

  updateEventStatus: (uuid: string, status: string) =>
    createAppRequest()<PortfolioEventDto>(`/bff/portfolio/events/${uuid}`, {
      method: 'PATCH',
      data: { status },
    }),

  updateReport: (uuid: string, data: Partial<Report>) =>
    createAppRequest()<Report>(`/bff/portfolio/reports/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listShareholders: (companyUuid: string, onlyCurrent = true) =>
    createAppRequest()<CompanyShareholderDto[]>(
      `/bff/investment/extractions/portfolio/companies/${companyUuid}/shareholders`,
      {
        params: { onlyCurrent: String(onlyCurrent) },
      },
    ),
};

export interface CompanyShareholderDto {
  uuid: string;
  tenantUuid?: string;
  companyUuid: string;
  shareholderName: string;
  shareholderType: string;
  shareholdingRatio: number;
  subscribedCapital: number;
  paidInCapital: number;
  currency: string;
  effectiveDate: string;
  isCurrent: boolean;
  sourceAttachmentUuid?: string;
  createdAt: string;
  updatedAt: string;
}

